import React, { FC, useState } from "react";
import Sort from "@components/Sort";
import { BiDotsVerticalRounded } from "react-icons/bi";
import useAppSelector from "@hooks/useAppSelector";
import { selectAllRecurringIssues } from "@store/recurringIssues/recurringIssueSelectors";
import useAppDispatch from "@hooks/useAppDispatch";
import { showPopUp } from "@store/ui/uiSlice";
import Table from "@components/base/table";
import { overrideTableStyles } from "@components/base/table/overrides";
import { css } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { EntityId } from "@reduxjs/toolkit";
import { selectSpaceEntities } from "@store/space/spaceSelectors";
import { format, isPast, isToday } from "date-fns";
import { getLanguage } from "@helpers";
import theme from "@styles/theme";
import Tooltip from "@components/Tooltip";
import { setSelectedRecurringIssueId } from "@store/recurringIssues/recurringIssueSlice";

interface RecurringIssueTableProps {
  loading?: boolean;
  search?: string;
}
const RecurringIssueTable: FC<RecurringIssueTableProps> = ({
  loading = false,
  search,
}) => {
  const { t } = useTranslation();
  const locale = getLanguage();
  const dispatch = useAppDispatch();
  const recurringIssues = useAppSelector(selectAllRecurringIssues);
  const allSpaces = useAppSelector(({ space }) => selectSpaceEntities(space));

  const [sort, setSort] = useState({ key: "nextDate", asc: true });
  const onChange = () => {
    setSort({ key: "nextDate", asc: !sort.asc });
  };

  const issuesSpaces = (spaces: EntityId[] | null) => {
    if (!spaces || !spaces.length) return "-";
    const sortedSpaces = spaces
      .map((spaceId) => allSpaces[spaceId]?.label)
      .sort((a, b) => (a && b ? a.localeCompare(b) : 0));

    const breakPoint = 5;
    const visible = sortedSpaces.slice(0, breakPoint).join(", ");
    const hidden = sortedSpaces.slice(breakPoint);

    if (hidden.length === 0) return <span>{visible}</span>;
    return (
      <div css={styles.container}>
        {visible}
        <span css={styles.hidden}>{hidden.length}</span>
        <div css={styles.tooltipWrapper}>
          <Tooltip>{hidden.join(", ")}</Tooltip>
        </div>
      </div>
    );
  };

  const getNextCreationDate = (date: Date | null) => {
    if (!date) return "-";

    if (isPast(date) && !isToday(date)) {
      return t("tasks.pastDate", {
        date: format(date, "d MMMM yyyy", { locale }),
      });
    }
    return format(date, "eeee d MMMM yyyy", { locale });
  };
  const generateRows =
    recurringIssues?.map((issue) => {
      const onShowPopUp = (
        event: React.MouseEvent<HTMLImageElement, MouseEvent>,
      ) => {
        dispatch(setSelectedRecurringIssueId(issue.id));
        dispatch(
          showPopUp({
            type: "recurringIssue/edit",
            pos: { x: event.clientX, y: event.clientY },
          }),
        );
      };

      return [
        <div css={styles.tableColumn}>
          {getNextCreationDate(issue.nextCreationDate)}
        </div>,
        <div css={styles.tableColumn}>{t(`tasks.${issue.issueType}`)}</div>,
        issue.label,
        issuesSpaces(issue?.spaceIds),
        <div css={styles.tableColumn}>
          {t(`frequencies.${issue.frequency}`, {
            count: issue.interval,
            date: getDateByFrequency(issue.startDate, issue.frequency),
          })}
        </div>,
        <div
          onClick={onShowPopUp}
          css={css({
            display: "flex",
            flex: 1,
            fontSize: "25px",
            justifyContent: "center",
            ":hover": {
              cursor: "pointer",
            },
          })}>
          <BiDotsVerticalRounded />
        </div>,
      ];
    }) ?? [];

  return (
    <Table
      columns={[
        <Sort id="nextDate" sort={sort} onChange={onChange}>
          {t("tasks.nextOccurrence")}
        </Sort>,
        <div id="type">{t("tasks.type")}</div>,
        <div id="name">{t("tasks.name")}</div>,
        <div id="locations">{t("tasks.locations")}</div>,
        <div id="frequency">{t("tasks.frequency")}</div>,
        <div id="actions">{t("tasks.actions")}</div>,
      ]}
      data={sort.asc ? generateRows : generateRows.reverse()}
      isLoading={loading}
      loadingMessage={t("common.loading")}
      emptyMessage={
        search?.length
          ? t("search.notFound", { search: t("common.tasks").toLowerCase() })
          : t("tasks.empty")
      }
      divider="horizontal"
      overrides={overrideTableStyles({
        TableHeadCell: {
          ":last-child": {
            width: "50px",
          },
        },
      })}
    />
  );
};

export default RecurringIssueTable;

const styles = {
  tableColumn: css({
    inlineSize: "max-content",
  }),

  container: css({
    cursor: "pointer",
    ":hover > div": {
      display: "flex !important",
    },
  }),

  tooltipWrapper: css({
    display: "none",
    top: 10,
  }),
  hidden: css({
    backgroundColor: theme.colors.orangeLight,
    padding: "2px 8px",
    borderRadius: "20px",
    marginLeft: "4px",
    ":before": {
      content: '"+"',
      marginRight: "4px",
    },
  }),
};

const getDateByFrequency = (date: Date, frequency: string): string => {
  switch (frequency) {
    case "weekly":
      return format(date, "EEEE", { locale: getLanguage() });
    case "monthly":
      return format(date, "do", { locale: getLanguage() });
    case "yearly":
      return format(date, "d MMMM", { locale: getLanguage() });

    default:
      return "";
  }
};
